import React, { type ReactElement, forwardRef } from 'react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import EditorCloseIcon from '@atlaskit/icon/core/migration/close--editor-close';
import Tooltip from '@atlaskit/tooltip';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	BannerWrapper,
	BannerHeader,
	BannerMessage,
	PinUnfilledStyled,
	BannerButtonContainer,
	BannerWrapperRow,
} from '../common/ui/styled.tsx';
import { useIsPinnedFieldsBannerVisible } from '../services/main.tsx';
import { messages } from './messages.tsx';

export type RenderProps = {
	onClick: () => void;
};

type Props = {
	children: (arg1: RenderProps) => ReactElement;
};

// Pinned Fields Banner Container, that takes care of
// closing, auto-closing and storing to preferences.
export const PinnedFieldsBannerContainer = ({ children }: Props) => {
	const [isVisible, setIsNotVisible] = useIsPinnedFieldsBannerVisible();
	if (!isVisible) return null;

	return children({ onClick: setIsNotVisible });
};

/**
 * Dismissable banner for when a user has no pinned fields.
 * @param param0.forceRender - Disregard the user's preference & render anyway. Used for
 *                             spotlight tours.
 */
export const PinnedFieldsBanner = forwardRef<HTMLDivElement, { forceRender?: boolean }>(
	({ forceRender = false }, ref) => {
		const { formatMessage } = useIntl();
		const [isVisible, setIsNotVisible] = useIsPinnedFieldsBannerVisible();
		if (!isVisible && !forceRender) return null;

		const dismissTooltipLabel = formatMessage(messages.dismissTooltip);
		return (
			<BannerWrapper ref={ref}>
				<BannerWrapperRow>
					<Heading as="div" size="xsmall">
						<BannerHeader>{formatMessage(messages.groupingTitle)}</BannerHeader>
					</Heading>
					<BannerMessage>
						<FormattedMessage
							{...messages.description}
							values={{
								pinnedIcon: <PinUnfilledStyled />,
							}}
						/>
					</BannerMessage>
				</BannerWrapperRow>
				<BannerButtonContainer>
					<Tooltip content={dismissTooltipLabel}>
						<Button
							spacing="none"
							appearance="subtle"
							onClick={setIsNotVisible}
							iconBefore={<EditorCloseIcon spacing="spacious" label={dismissTooltipLabel} />}
							testId="issue-view-layout-templates-pinned-fields-banner.ui.dismiss-button"
							interactionName="auto-generated.issue-view-layout-templates-pinned-fields-banner.ui.dismiss-button.button"
						/>
					</Tooltip>
				</BannerButtonContainer>
			</BannerWrapper>
		);
	},
);
